<template>
    <div
        :class="[
            'l-input-text',
            'form-group',
            { 'form-group-sm': size === 's' },
            'label-floating',
            { 'is-focused': isFocused },
            { 'remove-padding': (disabled || hideHelpBlock) && !error },
            { 'default-padding': defaultPadding },
            { 'has-error': error },
            { 'show-help': showHelpBlock },
            wrapperClass,
        ]"
        :data-test-class="`input-${type}`"
    >
        <label class="control-label" :for="id">
            {{ label }}
        </label>

        <div v-if="type === `textarea`">
            <textarea
                :id="id"
                :class="['form-control', inputClass]"
                :placeholder="placeholder"
                :value="value"
                :rows="rows"
                :disabled="disabled"
                :readonly="readonly"
                @focus="onFocus"
                @blur="onBlur"
                @input="onInput"
                @click="onClick"
                @keypress="onKeypress"
                @keyup="onKeyup"
            ></textarea>
            <span v-if="displayTextFormatHint" class="show text-format-hint pull-right">
                *<b>bold</b>* _<i>italics</i>_ ~<del>strike</del>~
                <pre>- list item</pre>
            </span>
        </div>

        <div v-else>
            <div :class="{ 'input-group': $slots.addon }">
                <input
                    :id="id"
                    :class="['form-control', inputClass]"
                    :type="type"
                    :placeholder="placeholder"
                    :value="value"
                    :min="minNumber"
                    :disabled="disabled"
                    :readonly="readonly"
                    @focus="onFocus"
                    @blur="onBlur"
                    @input="onInput"
                    @click="onClick"
                    @keypress="onKeypress"
                    @keyup="onKeyup"
                />
                <template v-if="$slots.addon">
                    <div class="input-group-addon ">
                        <slot name="addon"></slot>
                    </div>
                </template>
            </div>
        </div>

        <!-- Error -->
        <p v-show="error" class="help-block" data-test-error="">{{ errorMessage }}</p>
        <!-- /Error -->

        <!-- Custom Help Block -->
        <p v-show="showHelpBlock && helpBlockMessage && !error" class="help-block">
            {{ helpBlockMessage }}
        </p>
        <!-- /CustomHelp Block -->
    </div>
</template>

<script>
import generateDefaultId from "@/utils/generate-default-id";

export default {
    props: {
        label: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        value: {
            type: [String, Number],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: "text",
        },
        validator: {
            type: Function, // Returns `true` if value is valid
            default: () => true,
        },
        optional: {
            type: Boolean,
            default: false, // required by default
        },
        errorMessage: {
            type: String,
            default() {
                return this.$t("error.input.generic");
            },
        },
        showHelpBlock: {
            type: Boolean,
            default: false,
        },
        helpBlockMessage: {
            type: String,
            default: "",
        },
        hideHelpBlock: {
            type: Boolean,
            default: false,
        },
        defaultPadding: {
            type: Boolean,
            default: false,
        },
        id: {
            type: String,
            default() {
                return generateDefaultId();
            },
        },
        minNumber: {
            type: Number,
            default: 0,
        },
        rows: {
            type: Number,
            default: 2,
        },
        size: {
            type: String,
            default: "m", // enum ["s" | "m"]
        },
        wrapperClass: {
            type: String,
            default: "",
        },
        inputClass: {
            type: String,
            default: "",
        },
        showTextFormatHint: {
            type: Boolean,
            default: false,
        },
        onClick: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            isFocused: false,
            isModified: false,
            error: false,
            displayTextFormatHint: false,
        };
    },
    methods: {
        onKeypress(e) {
            this.$emit("keypress", e);
        },
        onKeyup(e) {
            this.$emit("keyup", e);
        },
        onInput(e) {
            this.isModified = true;

            // Auto-resize textarea
            if (this.type === "textarea") {
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
            }

            // Vue 2 .sync helper
            this.$emit("update:value", e.target.value);
            this.$emit("input", e.target.value);
        },
        onFocus() {
            this.isFocused = true;
            this.error = false;

            if (this.showTextFormatHint) {
                this.displayTextFormatHint = true;
            }

            this.$emit("focus");
        },
        onBlur() {
            this.isFocused = false;
            this.displayTextFormatHint = false;

            this.validate({ context: "blur" });

            this.$emit("blur");
        },

        /**
         * Validate the input value
         */
        validate({ context } = {}) {
            // if the value never got modified, don't show an error on blur
            if (context === "blur" && !this.isModified && this.value) return;

            let isValid;

            if (this.optional) {
                // If the input is optional, only run the validator function if there is a value.
                if (this.value) {
                    isValid = this.validator(this.value);
                } else {
                    isValid = true;
                }
            } else {
                // The input is required, so there needs to be a value and it needs to be validated
                //
                // Note: `this.value` is a String, thus we can check if it is falsy without compromising the value `0`
                //
                if (!this.value) {
                    isValid = false;
                } else {
                    isValid = this.validator(this.value);
                }
            }

            // We are setting the error here as the `validate()` function is being called through `$refs` from
            // the form, thus we need to also update the internal state of each input to display the
            // error state and message if needed.
            //
            this.error = !isValid;

            return isValid;
        },
    },
};
</script>

<style scoped>
.form-group {
    /* Extra padding-bottom is required to ensure there is enough room for the error text */
    padding-bottom: 20px;
    margin-top: 0px;
}

.form-group.default-padding {
    /* Revert back to the default padding */
    padding-bottom: 7px;
}
.form-group.remove-padding {
    /* Remove extra padding when input disabled */
    padding-bottom: 0;
}
.form-group.form-group-sm .form-control {
    margin-bottom: 7px;
}
.form-group .form-control.tw-mb-0 {
    margin-bottom: 0px;
}

.form-group.has-error .form-control {
    outline: none;
    background-size: 100% 2px, 100% 1px;
    background-image: linear-gradient(#f44336, #f44336), linear-gradient(#d2d2d2, #d2d2d2);
    transition-duration: 0.3s;
}
.form-group.has-error .help-block,
.form-group.show-help .help-block {
    display: block;
    margin-top: -8px;
}

.white-bg {
    background-color: #ffffff !important;
}
.white-bg::placeholder {
    color: #999999 !important;
}

/** Some defaults for usage in tailwind layouts */
.tw.form-group {
    line-height: 1;
}

.tw.form-group,
.tw.form-group .control-label {
    margin: 0;
    padding: 0;
}

.tw.form-group .form-control {
    margin: 0;
}

.tw label.control-label {
    top: 0 !important;
    position: unset !important;
    line-height: 1 !important;
    margin-bottom: 4px !important;
}

.tw .help-block {
    margin-top: 0 !important;
    position: unset !important;
    line-height: 20px !important;
}

.tw .text-format-hint.tw-opacity-20 {
    opacity: 0.2;
}

/** keep to not break existing layouts, default styles should not apply */
.survey-answer-flow .control-label {
    /** creates empty space otherwise */
    display: none;
}

.survey-answer-flow .text-format-hint {
    margin-top: 8px;
}

.survey-answer-flow.has-error .help-block {
    position: absolute !important;
    top: 100%;
    margin-bottom: 0;
}

/* Bootstrap overrides, can be removed after adding tailwind JIT */
.\!tw-bg-yellow-100 {
    background-color: #fef3c7 !important;
}
</style>
